
/**
 * Feature access matrix defining what features are available at each subscription level
 */

import { UserRole, SubscriptionTier } from '@/types/user.types';

type AccessLevel = UserRole | 'free' | 'basic' | 'premium' | 'subscriber' | 'staff_admin' | 'super_admin';

type AccessMatrix<T> = {
  [feature: string]: {
    [subfeature: string]: {
      [level in AccessLevel]?: T;
    };
  };
};

export const FEATURE_ACCESS_MATRIX: AccessMatrix<boolean | number> = {
    prayers: {
      create: {
        free: true,
        basic: true,
        premium: true,
        staff_admin: true,
        super_admin: true,
        subscriber: true,
      },
      max_count: {
        free: 5,
        basic: 100,
        premium: -1, // unlimited
        staff_admin: -1, // unlimited
        super_admin: -1, // unlimited
        subscriber: 20,
      },
      share_to_community: {
        free: false,
        basic: true,
        premium: true,
        staff_admin: true,
        super_admin: true,
        subscriber: true,
      }
  },
  groups: {
    create: {
      free: false,
      basic: true,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: true,
    },
    join: {
      free: true,
      basic: true,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: true,
    },
    max_join_count: {
      free: 2,
      basic: 5,
      premium: -1, // unlimited
      staff_admin: -1, // unlimited
      super_admin: -1, // unlimited
      subscriber: 5,
    }
  },
  analytics: {
    basic: {
      free: false,
      basic: true,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: false,
    },
    advanced: {
      free: false,
      basic: false,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: false,
    }
  },
  reminders: {
    basic: {
      free: true,
      basic: true,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: true,
    },
    custom: {
      free: false,
      basic: true,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: false,
    }
  },
  ai_features: {
    bible_translation: {
      free: false,
      basic: true,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: true,
    },
    prayer_guides: {
      free: false,
      basic: false,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: false,
    }
  },
  community_support: {
    prayer_partner: {
      free: false,
      basic: true,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: false,
    },
    mentorship: {
      free: false,
      basic: false,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: false,
    },
    spiritual_counseling: {
      free: false,
      basic: false,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: false,
    }
  },
  prayer_companion: {
    cards_mode: {
      free: true,
      basic: true,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: true,
    },
    chat_mode: {
      free: true,
      basic: true,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: true,
    },
    prayer_mode: {
      free: true,
      basic: true,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: false,
    },
    realtime_mode: {
      free: false,
      basic: false,
      premium: true,
      staff_admin: true,
      super_admin: true,
      subscriber: false,
    },
    daily_interactions: {
      free: 3,
      basic: 30,
      premium: -1, // unlimited
      staff_admin: -1, // unlimited
      super_admin: -1, // unlimited
      subscriber: 10,
    }
  }
};

/**
 * Function to check if a specific feature is available for a given access level
 */
export function hasAccess(
  featureCategory: string, 
  featureAction: string, 
  accessLevel: AccessLevel
): boolean {
  if (!FEATURE_ACCESS_MATRIX[featureCategory]?.[featureAction]) {
    return false;
  }
  
  const access = FEATURE_ACCESS_MATRIX[featureCategory][featureAction][accessLevel];
  return typeof access === 'boolean' ? access : access !== undefined;
}

/**
 * Function to get numeric limit for a given feature and access level
 */
export function getLimit(
  featureCategory: string, 
  featureAction: string, 
  accessLevel: AccessLevel
): number {
  if (!FEATURE_ACCESS_MATRIX[featureCategory]?.[featureAction]) {
    return 0;
  }
  
  const limit = FEATURE_ACCESS_MATRIX[featureCategory][featureAction][accessLevel];
  return typeof limit === 'number' ? limit : 0;
}

/**
 * Calculate comprehensive feature access for a user based on their subscription tier and role
 */
export function calculateFeatureAccess(
  subscriptionTier: 'free' | 'basic' | 'premium' = 'free',
  userRole: 'free' | 'basic' | 'premium' | 'subscriber' | 'staff_admin' | 'super_admin' = 'free'
): FeatureAccess {
  // Use the higher access level between subscription and role
  const accessLevel = getEffectiveAccessLevel(subscriptionTier, userRole);
  
  return {
    prayers: {
      canCreate: hasAccess('prayers', 'create', accessLevel),
      maxCount: getLimit('prayers', 'max_count', accessLevel),
      canShareToCommunity: hasAccess('prayers', 'share_to_community', accessLevel),
    },
    groups: {
      canCreate: hasAccess('groups', 'create', accessLevel),
      canJoin: hasAccess('groups', 'join', accessLevel),
      maxJoinCount: getLimit('groups', 'max_join_count', accessLevel),
    },
    analytics: {
      hasBasicAccess: hasAccess('analytics', 'basic', accessLevel),
      hasAdvancedAccess: hasAccess('analytics', 'advanced', accessLevel),
    },
    reminders: {
      hasBasicAccess: hasAccess('reminders', 'basic', accessLevel),
      hasCustomAccess: hasAccess('reminders', 'custom', accessLevel),
    },
    aiFeatures: {
      hasBibleTranslation: hasAccess('ai_features', 'bible_translation', accessLevel),
      hasPrayerGuides: hasAccess('ai_features', 'prayer_guides', accessLevel),
    },
    communitySupport: {
      hasPrayerPartner: hasAccess('community_support', 'prayer_partner', accessLevel),
      hasMentorship: hasAccess('community_support', 'mentorship', accessLevel),
      hasSpiritualCounseling: hasAccess('community_support', 'spiritual_counseling', accessLevel),
    },
    prayerCompanion: {
      canAccessCards: hasAccess('prayer_companion', 'cards_mode', accessLevel),
      canAccessChat: hasAccess('prayer_companion', 'chat_mode', accessLevel),
      canAccessPrayer: hasAccess('prayer_companion', 'prayer_mode', accessLevel),
      canAccessRealtime: hasAccess('prayer_companion', 'realtime_mode', accessLevel),
      dailyInteractionLimit: getLimit('prayer_companion', 'daily_interactions', accessLevel),
    },
  };
}

/**
 * Get the effective access level by comparing subscription and role
 */
function getEffectiveAccessLevel(
  subscriptionTier: 'free' | 'basic' | 'premium',
  userRole: 'free' | 'basic' | 'premium' | 'subscriber' | 'staff_admin' | 'super_admin'
): AccessLevel {
  // Admin roles always take precedence
  if (userRole === 'super_admin' || userRole === 'staff_admin') {
    return userRole;
  }
  
  // Priority order: premium > basic > subscriber > free
  const tierPriority = {
    premium: 4,
    basic: 3,
    subscriber: 2,
    free: 1
  };
  
  const subPriority = tierPriority[subscriptionTier] || 1;
  const rolePriority = tierPriority[userRole as keyof typeof tierPriority] || 1;
  
  if (subPriority >= rolePriority) {
    return subscriptionTier;
  } else {
    return userRole as AccessLevel;
  }
}

export interface FeatureAccess {
  prayers: {
    canCreate: boolean;
    maxCount: number;
    canShareToCommunity: boolean;
  };
  groups: {
    canCreate: boolean;
    canJoin: boolean;
    maxJoinCount: number;
  };
  analytics: {
    hasBasicAccess: boolean;
    hasAdvancedAccess: boolean;
  };
  reminders: {
    hasBasicAccess: boolean;
    hasCustomAccess: boolean;
  };
  aiFeatures: {
    hasBibleTranslation: boolean;
    hasPrayerGuides: boolean;
  };
  communitySupport: {
    hasPrayerPartner: boolean;
    hasMentorship: boolean;
    hasSpiritualCounseling: boolean;
  };
  prayerCompanion: {
    canAccessCards: boolean;
    canAccessChat: boolean;
    canAccessPrayer: boolean;
    canAccessRealtime: boolean;
    dailyInteractionLimit: number;
  };
}
